import React, { useMemo, useState, useEffect } from "react";
import { CCard, CCardGroup } from "@coreui/react";
import TranslationForm from "../translationForm";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import { translation } from "@/constants/translation/translation.translation";
import { messages } from "@/constants/translation/common.translation";
import validationHelper from "@/helpers/validationHelper";

/**
 * @name AddTranslation
 *
 * @description
 * The AddTranslation component provides a form for users to add new translations.
 * It retrieves the list of available languages and initializes the form fields accordingly.
 *
 * The component:
 * - Loads the list of supported languages.
 * - Initializes form fields with empty translation values.
 * - Submits new translations to the API.
 * - Displays success messages and redirects to the translations list upon successful submission.
 *
 * @returns {JSX.Element} The JSX for rendering the translation addition page.
 */

const AddTranslation = () => {
  const t = useTranslationContext(); // Fetching translation context for multi-language support
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    // Fetch languages on component mount
    const fetchLanguages = async () => {
      try {
        // Fetch languages from API
        const languagesResponse = await TranslationService.getLanguages();
        
        // Handle the actual API response structure: { message: '...', data: { "0": {...}, "1": {...} } }
        const languagesData = languagesResponse.data || languagesResponse;
        let languagesList = [];
        
        if (Array.isArray(languagesData)) {
          languagesList = languagesData;
        } else if (languagesData && typeof languagesData === 'object') {
          // Handle object with numeric keys like { "0": {...}, "1": {...} }
          // This is the actual structure from your API
          languagesList = Object.values(languagesData).map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code,
            isDefault: lang.isDefault,
            displayName: lang.name // Use the actual language name
          }));
        }
        
        setLanguages(languagesList);
      } catch (error) {
        toast.error(t(messages.toast.translation.languagesFetchFailed));
        console.error("Fetch languages error:", error);
        // Fallback to mock languages if API fails
      }
    };
    
    fetchLanguages();
  }, []);

  const handleAddTranslation = async (values) => {
    setIsLoading(true);
    try {
      const response = await TranslationService.add(values);
      toast.success(response.message || t(messages.toast.translation.added));
      navigate("/masters/translation/list");
    } catch (error) {
      toast.error(t(messages.toast.translation.addFailed));
      console.error("Add translation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = useMemo(() => {
    // If languages are not loaded yet, return a basic structure
    if (languages.length === 0) {
      return {
        key: "",
        translations: []
      };
    }
    
    return {
      key: "",
      translations: languages.map((lang) => ({
        title: "",
        language: lang.name, // Use actual language name
        language_id: lang.id,
        displayName: lang.name
      })),
    };
  }, [languages]);
  
  const validationSchema = useMemo(
    () => validationHelper.translationValidation(),
    []
  );
  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(translation.add.main_heading)}</h1>
        {!isLoading && (
          <TranslationForm
            t={t}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleAddTranslation}
            isLoading={isLoading}
          />
        )}
      </CCard>
    </CCardGroup>
  );
};

export default AddTranslation;
